import { createMachine } from 'xstate';
import { useActor } from '@xstate/react';
import { useContext } from 'react';
import { Typegen0 } from './state-machine.typegen';
import { assign } from '@xstate/immer';
import {
  OneRevealContext,
  oneRevealMachine,
  ONE_REVEAL_MACHINE,
} from './OneReveal/one-reveal-machine';
import {
  ManyRevealContext,
  manyRevealMachine,
  MANY_REVEAL_MACHINE,
} from './ManyReveal/many-reveal-machine';
import { hatchEggs } from './fetchers';
import { HatcheningContext } from './context';
import {
  CollectionEgg,
  CollectionGribble,
  EggCollectionResponse,
} from 'components/Collection/model';
import { fetchEggCollection } from './fetchers';
import { getLogin } from 'utils/API';

export const MAX_CONCURRENT_HATCH_LIMIT = 5;

type Selectable<T> = { item: T; selected: boolean };

type Trait = { traitType: string; value: string | number; revealed: boolean };

export function useHatchening() {
  return useActor(useContext(HatcheningContext));
}

export type GribbleState = {
  id: number;
  properties: Trait[];
  colors: Trait[];
};

export type RevealedGribble = Omit<CollectionGribble, keyof GribbleState> & GribbleState;

export type HatcheningContext = {
  hatchingStatus: { type: 'valid' } | { type: 'missingEggs' } | { type: 'unhatchableEgg' };
  eggs: Selectable<CollectionEgg>[];
  eggCursor: number | null;
  defaultEggId: number | null;
  // this is populated after receiving a response
  // from the server regarding hatching
  hatchingGribbles?: RevealedGribble[];
  drawerOpen: boolean;
  canFetchMoreEggs: boolean;
};

const defaultContext: HatcheningContext = {
  hatchingStatus: { type: 'missingEggs' },
  eggs: [],
  defaultEggId: null,
  eggCursor: null,
  hatchingGribbles: undefined,
  drawerOpen: false,
  canFetchMoreEggs: true,
};

type HatcheningEvent =
  | {
      type: 'toggleEgg';
      egg: CollectionEgg;
    }
  | { type: 'holdToHatch' }
  | { type: 'loadMoreEggs' }
  | { type: 'animationEnd' }
  | { type: 'toggleDrawer' };

// State Visualization
// https://stately.ai/registry/editor/bf4e8b71-e6fb-4ebc-9093-252c926b7b1c?machineId=1572276c-1c32-4e0a-ab45-ed5d81443c22
export const hatcheningMachine = createMachine(
  {
    context: defaultContext,
    tsTypes: {} as import('./state-machine.typegen').Typegen0,
    schema: {
      context: {} as HatcheningContext,
      services: {} as {
        hatchEggs: {
          data: RevealedGribble[];
        };
        getEggs: {
          // The data that gets returned from the service
          data: EggCollectionResponse;
        };
      },
      events: {} as HatcheningEvent,
    },
    preserveActionOrder: true,
    predictableActionArguments: true,
    id: 'Hatchening',
    initial: 'loadingEggs',
    states: {
      // initial state always starts with the loading of eggs
      loadingEggs: {
        invoke: {
          src: 'getEggs',
          onDone: [
            {
              target: 'canSelectEggs',
              actions: 'loadEggs',
            },
          ],
        },
      },
      // user is conditionally forwarded to the no egg page
      // before allowing them to select eggs if they have none
      canSelectEggs: {
        always: [
          {
            target: 'noEggsToHatch',
            cond: 'hasNoEggs',
          },
          {
            target: 'selectingEggs',
          },
        ],
      },
      // has eggs but no eggs selected
      noEggsToHatch: {
        type: 'final',
      },
      selectingEggs: {
        entry: 'checkHatchRequirements',
        on: {
          toggleEgg: [
            {
              target: 'selectingEggs',
              cond: 'eggAlreadySelected',
              actions: 'removeEgg',
            },
            {
              cond: 'canAddMoreEggs',
              actions: 'addEgg',
              target: 'selectingEggs',
            },
          ],
          loadMoreEggs: {
            cond: (ctx) => ctx.canFetchMoreEggs,
            target: 'loadingEggs',
          },
          toggleDrawer: {
            actions: 'toggleDrawer',
          },
          holdToHatch: {
            target: 'hatchingEggs',
          },
        },
      },
      hatchingEggs: {
        invoke: {
          src: 'hatchEggs',
          onDone: [{ actions: 'receiveGribbles', target: '.done' }],
        },
        initial: 'fetching',
        // we want to show the spinner 1 second after
        // transitioning to the `hatchingEggs` state.
        // if we don't want the delay we can simply use the
        // hatchingEggs.waiting state to determine when to show a spinner
        after: {
          '1000': {
            target: '.waiting',
            internal: true,
          },
        },
        states: {
          fetching: {},
          waiting: {},
          done: {
            type: 'final',
          },
        },
        onDone: {
          target: 'toReveal',
        },
      },
      toReveal: {
        always: [
          {
            // needed to allow the animation of single card selection
            // to take place that is not relevant for multiple card selection
            target: 'playingAnimation',
            cond: 'hasOneEggSelected',
          },
          {
            target: 'manyRevealMachine',
          },
        ],
      },
      playingAnimation: {
        on: {
          animationEnd: {
            target: 'oneRevealMachine',
          },
        },
      },
      // invoking a nested machine to go through the reveal process
      // and come back to the beginning once it's finished
      oneRevealMachine: {
        invoke: {
          src: 'revealOne',
          id: ONE_REVEAL_MACHINE,
          data(ctx): OneRevealContext {
            if (!ctx.hatchingGribbles || ctx.hatchingGribbles.length === 0) {
              throw Error('Attempting to reveal with 0 hatching gribbles?');
            }
            // Reset cursor to reload eggs on 'onDone'
            ctx.eggCursor = null;
            ctx.eggs = [];
            return {
              gribble: ctx.hatchingGribbles[0],
            };
          },
          onDone: [
            {
              actions: 'resetState',
              target: 'loadingEggs',
            },
          ],
        },
      },
      manyRevealMachine: {
        invoke: {
          src: 'revealMany',
          id: MANY_REVEAL_MACHINE,
          data(ctx): ManyRevealContext {
            if (!ctx.hatchingGribbles || ctx.hatchingGribbles.length <= 0) {
              throw Error('Attempting to reveal with fewer than 2 hatching gribbles?');
            }
            // Reset cursor to reload eggs on 'onDone'
            ctx.eggCursor = null;
            ctx.eggs = [];
            return {
              gribbles: ctx.hatchingGribbles,
              page: 0,
            };
          },
          onDone: [
            {
              actions: 'resetState',
              target: 'loadingEggs',
            },
          ],
        },
      },
    },
  },
  {
    services: {
      revealOne: oneRevealMachine,
      revealMany: manyRevealMachine,
      async getEggs(ctx) {
        const login = getLogin();
        ctx.canFetchMoreEggs = false;
        const collection = await fetchEggCollection(login?.userId, ctx.eggCursor);
        return {
          ...collection,
          // sorting the result by hatching status to make sure
          // hatching eggs stay at the bottom & don't clutter up the UI
          items: collection.items.sort((egg) => (egg.hatching ? 1 : -1)),
        };
      },
      async hatchEggs(ctx) {
        const login = getLogin();
        if (!login) {
          // this should not be possible to reach as logged out users
          // can't reach this state
          throw new Error('User is not logged in. Invalid state');
        }
        const hatchableEggs = ctx.eggs.filter(({ selected }) => selected).map(({ item }) => item);
        return hatchEggs(login.userId, hatchableEggs);
      },
    },
    actions: {
      loadEggs: assign((ctx, eggs) => {
        ctx.eggCursor = eggs.data.cursor;
        ctx.eggs = ctx.eggs.concat(
          eggs.data.items.map((item) => ({ selected: item.id === ctx.defaultEggId, item })),
        );
        // this is not affected by the initial null cursor because
        // it will always at least have the first fetch's cursor result
        ctx.canFetchMoreEggs = ctx.eggCursor !== null;
      }),
      checkHatchRequirements: assign((ctx) => {
        const oneUnhatchableEggSelected = ctx.eggs.some((egg) => egg.selected && egg.item.hatching);
        if (oneUnhatchableEggSelected) {
          ctx.hatchingStatus = { type: 'unhatchableEgg' };
          return;
        }

        const noEggsSelected = ctx.eggs.every((egg) => !egg.selected);

        if (noEggsSelected) {
          ctx.hatchingStatus = { type: 'missingEggs' };
          return;
        }
        ctx.hatchingStatus = { type: 'valid' };
      }),
      receiveGribbles: assign((ctx, payload) => {
        ctx.hatchingGribbles = payload.data;
      }),
      removeEgg: assign((ctx, payload) => {
        const target = ctx.eggs.find((egg) => egg.item.id === payload.egg.id);
        if (target) {
          target.selected = false;
        }
      }),
      addEgg: assign((ctx, payload) => {
        const target = ctx.eggs.find((egg) => egg.item.id === payload.egg.id);
        if (target) {
          target.selected = true;
        }
      }),
      toggleDrawer: assign((ctx) => {
        ctx.drawerOpen = !ctx.drawerOpen;
      }),
      resetState: () => defaultContext,
    },
    guards: {
      hasNoEggs(ctx) {
        return ctx.eggs.length === 0;
      },
      eggAlreadySelected(ctx, eggToAdd) {
        return ctx.eggs.some(({ item, selected }) => selected && item.id === eggToAdd.egg.id);
      },
      hasOneEggSelected(ctx) {
        return ctx.eggs.filter(({ selected }) => selected).length === 1;
      },
      canAddMoreEggs: (ctx) =>
        ctx.eggs.filter(({ selected }) => selected).length < MAX_CONCURRENT_HATCH_LIMIT,
    },
  },
);

export type HatcheningState = Typegen0['matchesStates'];
